import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LogOut,
  ExternalLink,
  Users,
  Clock,
  ClipboardList,
  CalendarDays,
  CalendarClock,
  Truck,
  Wrench,
  Bell,
  ShieldCheck,
  History,
  Mail,
  FileText,
  BookOpen,
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useErpAuth } from "@/context/ErpAuthContext";
import AngajatiTab from "@/components/erp/AngajatiTab";
import RoluriTab from "@/components/erp/RoluriTab";
import TrasabilitateTab from "@/components/erp/TrasabilitateTab";
import PontajTab from "@/components/erp/PontajTab";
import EvidentaOreTab from "@/components/erp/EvidentaOreTab";
import ConcediiTab from "@/components/erp/ConcediiTab";
import InvoiriTab from "@/components/erp/InvoiriTab";
import ReglementariTab from "@/components/erp/ReglementariTab";
import { ChangeOwnPassword } from "@/components/erp/ChangeOwnPassword";

export default function ErpDashboard() {
  const { user, logout, hasPerm } = useErpAuth();
  const [active, setActive] = useState(() => {
    const order = [
      ["angajati", "angajati.vizualizare"],
      ["pontaj", "pontaj.vizualizare"],
      ["evidenta", "pontaj.vizualizare"],
      ["concedii", "concedii.vizualizare"],
      ["invoiri", "invoiri.vizualizare"],
      ["reglementari", "reglementari.vizualizare"],
      ["roluri", "setari.roluri"],
      ["trasabilitate", "setari.trasabilitate"],
    ];
    const found = order.find(([, p]) => hasPerm(p));
    return found ? found[0] : "angajati";
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const SECTIONS = [
    {
      title: "Personal",
      items: [
        { id: "angajati", label: "Angajați", icon: Users, perm: "angajati.vizualizare" },
        { id: "pontaj", label: "Pontaj", icon: Clock, perm: "pontaj.vizualizare" },
        { id: "evidenta", label: "Evidență Ore", icon: ClipboardList, perm: "pontaj.vizualizare" },
        { id: "concedii", label: "Concedii", icon: CalendarDays, perm: "concedii.vizualizare" },
        { id: "invoiri", label: "Învoiri", icon: CalendarClock, perm: "invoiri.vizualizare" },
      ],
    },
    {
      title: "Documente",
      items: [
        { id: "reglementari", label: "Reglementări Interne", icon: BookOpen, perm: "reglementari.vizualizare" },
      ],
    },
    {
      title: "Operațional",
      items: [
        { id: "flota", label: "Flotă", icon: Truck, soon: true },
        { id: "echipamente", label: "Echipamente", icon: Wrench, soon: true },
        { id: "alerte", label: "Alerte", icon: Bell, soon: true },
      ],
    },
    {
      title: "Setări",
      items: [
        { id: "roluri", label: "Administrare roluri", icon: ShieldCheck, perm: "setari.roluri" },
        { id: "trasabilitate", label: "Trasabilitate", icon: History, perm: "setari.trasabilitate" },
        { id: "email", label: "Notificări e-mail", icon: Mail, soon: true },
        { id: "pdf", label: "Formulare PDF", icon: FileText, soon: true },
      ],
    },
  ];

  const titleFor = {
    angajati: "Angajați",
    pontaj: "Pontaj",
    evidenta: "Evidență Ore",
    concedii: "Concedii",
    invoiri: "Învoiri",
    reglementari: "Reglementări Interne",
    roluri: "Administrare roluri",
    trasabilitate: "Trasabilitate",
  };

  const go = (item) => {
    if (item.soon) return;
    setActive(item.id);
    setMobileOpen(false);
  };

  const canSee = (item) => !item.soon && (!item.perm || hasPerm(item.perm));

  const SidebarInner = () => (
    <div className="flex flex-col h-full bg-white">
      <div className="px-6 py-5 border-b border-slate-200">
        <p className="font-display font-extrabold text-xl text-slate-800">
          Sim<span className="text-[#2563EB]">Manager</span>{" "}
          <span className="text-slate-400 text-sm font-semibold">Angajați</span>
        </p>
        <p className="text-sm text-slate-600 mt-1 truncate">Bun venit, {user?.name}</p>
        <p className="text-xs text-slate-400 font-mono truncate">{user?.email}</p>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
        {SECTIONS.map((section) => {
          const visibleItems = section.items.filter(canSee);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title}>
              <p className="px-3 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                {section.title}
              </p>
              <div className="space-y-1">
                {visibleItems.map((n) => {
                  const Icon = n.icon;
                  const isActive = active === n.id;
                  return (
                    <button
                      key={n.id}
                      onClick={() => go(n)}
                      disabled={n.soon}
                      data-testid={`erp-tab-${n.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                        isActive
                          ? "bg-[#2563EB] text-white"
                          : n.soon
                          ? "text-slate-300 cursor-not-allowed"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <span className="flex-1 text-left">{n.label}</span>
                      {n.soon && (
                        <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">
                          curând
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-slate-200 space-y-1">
        <ChangeOwnPassword />
        <Link to="/" target="_blank" className="block">
          <span className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors duration-200">
            <ExternalLink className="w-5 h-5 shrink-0" /> Vezi site-ul
          </span>
        </Link>
        <button
          onClick={logout}
          data-testid="erp-logout-button"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-[#DC2626] hover:text-white transition-colors duration-200"
        >
          <LogOut className="w-5 h-5 shrink-0" /> Deconectare
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F9] flex">
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 shadow-lg fixed inset-y-0 left-0 z-40">
        <SidebarInner />
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden bg-white text-slate-800 border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-4 h-14">
          <p className="font-display font-extrabold">
            Sim<span className="text-[#2563EB]">Manager</span> Angajați
          </p>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="erp-mobile-menu-button"
                className="text-slate-700 hover:bg-slate-100"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-white border-none text-slate-800">
              <SidebarInner />
            </SheetContent>
          </Sheet>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B1424] mb-6">
            {titleFor[active]}
          </h1>
          {active === "angajati" && <AngajatiTab />}
          {active === "pontaj" && <PontajTab />}
          {active === "evidenta" && <EvidentaOreTab />}
          {active === "concedii" && <ConcediiTab />}
          {active === "invoiri" && <InvoiriTab />}
          {active === "reglementari" && <ReglementariTab />}
          {active === "roluri" && <RoluriTab />}
          {active === "trasabilitate" && <TrasabilitateTab />}
        </main>
      </div>
    </div>
  );
}
